import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { Folder, Users, MoreVertical, Calendar } from 'lucide-react';
import { Board } from '../../types/common';
import EditBoardModal from './EditBoardModal';
import DeleteBoardModal from './DeleteBoardModal';
import './BoardList.scss';

interface BoardListProps {
  workspaceId: string;
  boards: Board[];
  loading: boolean;
  isOwnerOrAdmin: boolean;
}

const BoardList: React.FC<BoardListProps> = ({ workspaceId, boards, loading, isOwnerOrAdmin }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [editBoardModalOpen, setEditBoardModalOpen] = useState(false);
  const [deleteBoardModalOpen, setDeleteBoardModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBoards = searchQuery.trim()
    ? boards.filter(
        (board) =>
          board.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
          board.key?.toLowerCase().includes(searchQuery.trim().toLowerCase()),
      )
    : boards;

  const handleBoardClick = (board: Board) => {
    navigate(`/boards/${board.id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, board: Board) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBoard(board);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewBoard = () => {
    if (selectedBoard) {
      navigate(`/boards/${selectedBoard.id}`);
    }
    handleMenuClose();
  };

  const handleEditBoard = () => {
    setEditBoardModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteBoard = () => {
    setDeleteBoardModalOpen(true);
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="board-list">
      {boards.length === 0 ? (
        <Box className="board-list-empty">
          <Folder size={64} strokeWidth={1} />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            No boards yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first board to start organizing tasks
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Boards</Typography>
            <TextField
              size="small"
              placeholder="Search boards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 240 }}
            />
          </Box>
          {filteredBoards.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No boards match "{searchQuery}"
            </Typography>
          ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {filteredBoards.map((board) => (
            <Box key={board.id}>
              <Card
                className="board-card"
                onClick={() => handleBoardClick(board)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  },
                }}
              >
                <Box
                  className="board-card-header"
                  sx={{
                    height: 8,
                    backgroundColor: board.color || '#0073ea',
                  }}
                />
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {board.key && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {board.key}
                        </Typography>
                      )}
                      <Typography variant="h6" component="h3" noWrap>
                        {board.name}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, board)}
                      sx={{ ml: 1 }}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      height: 40,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {board.description || 'No description'}
                  </Typography>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Users size={16} />
                    <Typography variant="caption" color="text.secondary">
                      {board.members?.length || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                    <Calendar size={16} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(board.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
          )}
        </>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewBoard}>View Board</MenuItem>
        {isOwnerOrAdmin && (
          <>
            <MenuItem onClick={handleEditBoard}>Edit Board</MenuItem>
            <MenuItem onClick={handleDeleteBoard} sx={{ color: 'error.main' }}>
              Delete Board
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Edit Board Modal */}
      <EditBoardModal
        open={editBoardModalOpen}
        onClose={() => {
          setEditBoardModalOpen(false);
          setSelectedBoard(null);
        }}
        board={selectedBoard}
        workspaceId={workspaceId}
      />

      {/* Delete Board Modal */}
      <DeleteBoardModal
        open={deleteBoardModalOpen}
        onClose={() => {
          setDeleteBoardModalOpen(false);
          setSelectedBoard(null);
        }}
        board={selectedBoard}
        workspaceId={workspaceId}
      />
    </Box>
  );
};

export default BoardList;
